import { request } from './client';

// Notification types matching backend
export type NotificationType =
  | 'hit_winner'
  | 'square_request_approved'
  | 'square_request_denied'
  | 'board_published'
  | 'numbers_randomized'
  | 'board_locked'
  | 'board_completed'
  | 'bad_numbers_payout'
  | 'neighbor_payout'
  | 'square_request_pending'
  | 'manager_approval_needed'
  | 'merge_request_pending'
  | 'merge_suggestion'
  | 'bulk_merge_suggestion'
  | 'system_promotion'
  | 'system_new_feature'
  | 'system_new_themes'
  | 'system_announcement';

export type NotificationCategory = 'all' | 'wins' | 'board_updates' | 'system';

export interface WinReason {
  type: 'main' | 'wrong_way' | 'plus_n' | 'plus_n_wrong_way' | 'neighbor' | 'box_neighbor';
  amount: number;
}

export interface NotificationMeta {
  boardName?: string;
  amount?: number;
  quarter?: number;
  quarterLabel?: string;
  winReasons?: WinReason[];
  squareCount?: number;
  denialReason?: string;
  couponCode?: string;
  featureName?: string;
  iconType?: string;
  hitId?: string;
  // Merge suggestion fields
  offlinePlayerId?: string;
  targetUserId?: string;
  similarityScore?: number;
  // Bulk merge suggestion fields
  boardCount?: number;
  matchCount?: number;
}

export interface Notification {
  id: string;
  userId?: string;
  boardId?: string;
  type: NotificationType;
  title: string;
  message: string;
  actionUrl?: string;
  actionLabel?: string;
  metadata: NotificationMeta;
  isRead: boolean;
  readAt?: string;
  expiresAt?: string;
  createdAt: string;
  board?: {
    id: string;
    name: string;
    slug?: string;
  };
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  limit: number;
  offset: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface CreateSystemNotificationInput {
  type: NotificationType;
  title: string;
  message: string;
  actionUrl?: string;
  actionLabel?: string;
  couponCode?: string;
  featureName?: string;
  expiresAt?: string;
}

export interface UpdateSystemNotificationInput {
  title?: string;
  message?: string;
  actionUrl?: string;
  actionLabel?: string;
  expiresAt?: string;
}

export const notificationsApi = {
  // Get paginated notifications for current user
  async list(params?: {
    category?: NotificationCategory;
    limit?: number;
    offset?: number;
  }): Promise<NotificationsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const url = queryString
      ? `/api/squareup/notifications?${queryString}`
      : '/api/squareup/notifications';

    return request<NotificationsResponse>(url);
  },

  // Get unread notification count
  async getUnreadCount(): Promise<UnreadCountResponse> {
    return request<UnreadCountResponse>('/api/squareup/notifications/unread-count');
  },

  // Mark a single notification as read
  async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(
      `/api/squareup/notifications/${notificationId}/read`,
      { method: 'PATCH' }
    );
  },

  // Mark all notifications as read
  async markAllAsRead(): Promise<{ success: boolean }> {
    return request<{ success: boolean }>('/api/squareup/notifications/read-all', {
      method: 'PATCH',
    });
  },

  // Admin: Get system notifications
  async adminList(params?: {
    limit?: number;
    offset?: number;
  }): Promise<NotificationsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const url = queryString
      ? `/api/squareup/admin/notifications?${queryString}`
      : '/api/squareup/admin/notifications';

    return request<NotificationsResponse>(url);
  },

  // Admin: Create system notification
  async adminCreate(input: CreateSystemNotificationInput): Promise<Notification> {
    return request<Notification>('/api/squareup/admin/notifications', {
      method: 'POST',
      body: input,
    });
  },

  // Admin: Update system notification
  async adminUpdate(
    notificationId: string,
    input: UpdateSystemNotificationInput
  ): Promise<Notification> {
    return request<Notification>(`/api/squareup/admin/notifications/${notificationId}`, {
      method: 'PATCH',
      body: input,
    });
  },

  // Admin: Delete system notification
  async adminDelete(notificationId: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(
      `/api/squareup/admin/notifications/${notificationId}`,
      { method: 'DELETE' }
    );
  },
};

// SSE connection for real-time notifications
export function createNotificationEventSource(
  onNotification: () => void,
  onError?: (error: Event) => void,
  onGlobalMergeRequestChange?: () => void
): EventSource {
  const url = '/api/squareup/notifications/events';
  const eventSource = new EventSource(url, { withCredentials: true });

  eventSource.addEventListener('connected', () => {
    console.log('[Notifications] SSE connected');
  });

  eventSource.addEventListener('notification', () => {
    // New notification received - trigger refresh
    onNotification();
  });

  // Global merge request events (sent to user's notification channel)
  eventSource.addEventListener('update', (e) => {
    try {
      const data = JSON.parse((e as MessageEvent).data);
      if (data.type === 'global_merge_request_created' || data.type === 'global_merge_request_cancelled') {
        console.log('[Notifications] Global merge request event:', data.type);
        onGlobalMergeRequestChange?.();
        // Also trigger notification refresh since these create/remove notifications
        onNotification();
      }
    } catch (_err) {
      // Ignore parse errors for non-JSON events
    }
  });

  eventSource.onerror = (error) => {
    console.error('[Notifications] SSE error:', error);
    onError?.(error);
  };

  return eventSource;
}
